<template>
  <v-row justify="center" class="ma-0">
    <v-col cols="12" lg="9" xl="8">

      <!-- ── Page header ── -->
      <div class="d-flex align-center mb-4">
        <v-btn icon small class="me-2" @click="$router.back()">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="text-h6 font-weight-bold ma-0">Add a Product</h1>
          <p class="text-caption grey--text ma-0">Fill in the steps below to publish a new product</p>
        </div>
        <v-spacer></v-spacer>
        <v-img
          :src="require(`@/assets/images/logos/logo.png`)"
          max-width="110px"
          contain
          class="d-none d-sm-flex"
        ></v-img>
      </div>

      <!-- ── Stepper card ── -->
      <v-card elevation="3" class="rounded-xl overflow-hidden">

        <!-- ──────────────────────────────────────
             STEP HEADER (progress + labels)
             ────────────────────────────────────── -->
        <v-sheet color="grey lighten-5" class="px-4 pt-4 pb-2">
          <!-- Progress bar -->
          <v-progress-linear
            :value="(currentStep / totalSteps) * 100"
            color="primary"
            background-color="grey lighten-3"
            rounded
            height="6"
            class="mb-3"
          ></v-progress-linear>

          <!-- Step pills -->
          <div class="d-flex justify-space-between step-header">
            <div
              v-for="(step, i) in steps"
              :key="i"
              class="step-item d-flex flex-column align-center"
              :class="{ 'step-active': currentStep === i + 1, 'step-done': currentStep > i + 1 }"
              @click="goToStep(i + 1)"
              style="cursor:pointer; flex:1"
            >
              <div class="step-circle d-flex align-center justify-center mb-1">
                <v-icon v-if="currentStep > i + 1" size="14" color="white">mdi-check</v-icon>
                <span v-else class="text-caption font-weight-bold">{{ i + 1 }}</span>
              </div>
              <span class="step-label text-center d-none d-sm-block">{{ step.label }}</span>
            </div>
          </div>
        </v-sheet>

        <v-divider></v-divider>

        <!-- ──────────────────────────────────────
             STEP 1 — Basic Info
             ────────────────────────────────────── -->
        <transition name="slide-fade" mode="out-in">
          <div v-if="currentStep === 1" key="step1" class="pa-4 pa-sm-6">
            <step-title icon="mdi-information-outline" title="Basic Information"
              subtitle="Name, category and supplier contact" />

            <v-row dense>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.selectedCategory"
                  :items="categoryItems"
                  item-text="name"
                  item-value="id"
                  outlined dense
                  :loading="categoriesLoading"
                  :no-data-text="categoriesLoading ? 'Loading…' : 'No categories'"
                  label="Product Category *"
                  prepend-inner-icon="mdi-tag-outline"
                  :error-messages="errors.selectedCategory"
                  @change="clearError('selectedCategory')"
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.product_name"
                  outlined dense
                  label="Product Name *"
                  prepend-inner-icon="mdi-package-variant"
                  :error-messages="errors.product_name"
                  @input="clearError('product_name')"
                  counter="120"
                  maxlength="120"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.supplier_contact"
                  outlined dense
                  label="WhatsApp Inquiry Contact *"
                  prepend-inner-icon="mdi-whatsapp"
                  type="number"
                  hint="Full number with country code e.g. 237691234567"
                  persistent-hint
                  :error-messages="errors.supplier_contact"
                  @input="clearError('supplier_contact')"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.key_words"
                  outlined dense
                  label="Keywords *"
                  prepend-inner-icon="mdi-tag-multiple-outline"
                  hint="Separate with commas: shoes, men, sport"
                  persistent-hint
                  :error-messages="errors.key_words"
                  @input="clearError('key_words')"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>
        </transition>

        <!-- ──────────────────────────────────────
             STEP 2 — Pricing & Stock
             ────────────────────────────────────── -->
        <transition name="slide-fade" mode="out-in">
          <div v-if="currentStep === 2" key="step2" class="pa-4 pa-sm-6">
            <step-title icon="mdi-currency-usd" title="Pricing & Stock"
              subtitle="Set your selling price, wholesale price and available stock" />

            <v-row dense>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="form.product_price"
                  outlined dense
                  label="Selling Price (XAF) *"
                  type="number"
                  min="0"
                  prepend-inner-icon="mdi-cash"
                  :error-messages="errors.product_price"
                  @input="clearError('product_price')"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="form.product_wholesale_price"
                  outlined dense
                  label="Buy Price (XAF) *"
                  type="number"
                  min="0"
                  prepend-inner-icon="mdi-cash-minus"
                  :error-messages="errors.product_wholesale_price"
                  @input="clearError('product_wholesale_price')"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="form.product_no_stock"
                  outlined dense
                  label="Number in Stock *"
                  type="number"
                  min="0"
                  prepend-inner-icon="mdi-package-variant-closed"
                  :error-messages="errors.product_no_stock"
                  @input="clearError('product_no_stock')"
                ></v-text-field>
              </v-col>

              <!-- Margin indicator -->
              <v-col cols="12" v-if="margin !== null">
                <v-alert
                  :type="margin >= 0 ? 'success' : 'warning'"
                  dense text border="left"
                  class="rounded-lg"
                >
                  <strong>Margin:</strong>
                  {{ formatPrice(Math.abs(margin)) }}
                  {{ margin >= 0 ? 'profit' : 'loss' }} per unit
                  <span v-if="form.product_price && form.product_wholesale_price" class="ms-1">
                    ({{ marginPct }}%)
                  </span>
                </v-alert>
              </v-col>
            </v-row>
          </div>
        </transition>

        <!-- ──────────────────────────────────────
             STEP 3 — Media
             ────────────────────────────────────── -->
        <transition name="slide-fade" mode="out-in">
          <div v-if="currentStep === 3" key="step3" class="pa-4 pa-sm-6">
            <step-title icon="mdi-image-multiple-outline" title="Product Media"
              subtitle="Cover image, extra photos and optional video" />

            <v-row dense>
              <!-- Cover image -->
              <v-col cols="12" md="6">
                <div class="media-zone rounded-xl pa-3 mb-1" :class="{ 'media-zone--error': errors.product_cover_image }">
                  <p class="text-caption font-weight-bold text-uppercase grey--text mb-2">
                    <v-icon size="14" class="me-1">mdi-image-outline</v-icon>Cover Image *
                  </p>
                  <div v-if="coverPreview" class="cover-preview-wrap mb-2">
                    <v-img :src="coverPreview" height="160" contain class="rounded-lg grey lighten-4"></v-img>
                    <v-btn fab x-small color="error" elevation="2" class="cover-remove-btn"
                      @click="removeCover">
                      <v-icon size="14">mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <v-file-input
                    v-model="form.product_cover_image"
                    show-size
                    accept="image/*"
                    prepend-icon=""
                    prepend-inner-icon="mdi-upload"
                    dense outlined
                    label="Choose cover image"
                    :error-messages="errors.product_cover_image"
                    @change="onCoverChange"
                    truncate-length="20"
                  ></v-file-input>
                </div>
              </v-col>

              <!-- Video (optional) -->
              <v-col cols="12" md="6">
                <div class="media-zone rounded-xl pa-3 mb-1">
                  <p class="text-caption font-weight-bold text-uppercase grey--text mb-2">
                    <v-icon size="14" class="me-1">mdi-video-outline</v-icon>Product Video
                    <v-chip x-small color="grey lighten-3" class="ms-1">Optional</v-chip>
                  </p>
                  <div v-if="videoPreview" class="mb-2">
                    <video :src="videoPreview" controls height="160"
                      style="width:100%;border-radius:8px;background:#000" preload="metadata"></video>
                    <v-btn x-small text color="error" class="mt-1" @click="removeVideo">
                      <v-icon left size="14">mdi-close</v-icon>Remove
                    </v-btn>
                  </div>
                  <v-file-input
                    v-model="form.product_video"
                    show-size
                    accept="video/*"
                    prepend-icon=""
                    prepend-inner-icon="mdi-upload"
                    dense outlined
                    label="Choose product video"
                    @change="onVideoChange"
                    truncate-length="20"
                  ></v-file-input>
                </div>
              </v-col>

              <!-- Extra images -->
              <v-col cols="12">
                <div class="media-zone rounded-xl pa-3" :class="{ 'media-zone--error': errors.product_images }">
                  <p class="text-caption font-weight-bold text-uppercase grey--text mb-2">
                    <v-icon size="14" class="me-1">mdi-image-multiple-outline</v-icon>Additional Images *
                  </p>

                  <!-- Thumbnails -->
                  <div v-if="imagePreviews.length" class="d-flex flex-wrap mb-2" style="gap:8px">
                    <div
                      v-for="(src, idx) in imagePreviews"
                      :key="idx"
                      class="extra-thumb-wrap"
                    >
                      <v-img :src="src" width="72" height="72" cover class="rounded-lg grey lighten-4"></v-img>
                      <v-btn fab x-small color="error" elevation="1" class="thumb-remove-btn"
                        @click="removeExtraImage(idx)">
                        <v-icon size="10">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <v-file-input
                    v-model="form.product_images"
                    show-size multiple
                    accept="image/*"
                    prepend-icon=""
                    prepend-inner-icon="mdi-upload"
                    dense outlined
                    label="Choose product images"
                    :error-messages="errors.product_images"
                    @change="onImagesChange"
                    truncate-length="20"
                  ></v-file-input>
                  <p class="text-caption grey--text mt-1">You can select multiple images at once</p>
                </div>
              </v-col>
            </v-row>
          </div>
        </transition>

        <!-- ──────────────────────────────────────
             STEP 4 — Description & Review
             ────────────────────────────────────── -->
        <transition name="slide-fade" mode="out-in">
          <div v-if="currentStep === 4" key="step4" class="pa-4 pa-sm-6">
            <step-title icon="mdi-text-box-outline" title="Description & Review"
              subtitle="Write a compelling product description then review before publishing" />

            <!-- Description -->
            <div class="mb-5">
              <p class="text-body-2 font-weight-bold mb-2">Product Description *</p>
              <vue-editor v-model="form.product_description" />
              <p v-if="errors.product_description" class="error--text text-caption mt-1">
                {{ errors.product_description }}
              </p>
            </div>

            <!-- Review summary -->
            <v-divider class="mb-4"></v-divider>
            <p class="text-subtitle-2 font-weight-bold text-uppercase grey--text mb-3">
              <v-icon size="16" class="me-1">mdi-eye-outline</v-icon>Review Summary
            </p>
            <v-row dense>
              <v-col cols="12" sm="6">
                <review-row label="Name" :value="form.product_name" />
                <review-row label="Category" :value="categoryName" />
                <review-row label="Contact" :value="form.supplier_contact" />
                <review-row label="Keywords" :value="form.key_words" />
              </v-col>
              <v-col cols="12" sm="6">
                <review-row label="Sell Price" :value="formatPrice(form.product_price)" />
                <review-row label="Buy Price"  :value="formatPrice(form.product_wholesale_price)" />
                <review-row label="Stock"      :value="form.product_no_stock + ' units'" />
                <review-row label="Cover"      :value="form.product_cover_image ? form.product_cover_image.name : '—'" />
                <review-row label="Extra imgs" :value="form.product_images.length + ' file(s)'" />
                <review-row label="Video"      :value="form.product_video ? form.product_video.name : 'None'" />
              </v-col>
            </v-row>
          </div>
        </transition>

        <!-- ──────────────────────────────────────
             NAV BUTTONS
             ────────────────────────────────────── -->
        <v-divider></v-divider>
        <div class="d-flex align-center justify-space-between pa-4">
          <v-btn
            text rounded
            class="text-none font-weight-bold"
            :disabled="currentStep === 1 || loading"
            @click="prevStep"
          >
            <v-icon left size="18">mdi-arrow-left</v-icon>Back
          </v-btn>

          <span class="text-caption grey--text">Step {{ currentStep }} of {{ totalSteps }}</span>

          <div>
            <!-- Last step → submit -->
            <v-btn
              v-if="currentStep === totalSteps"
              color="success"
              rounded
              elevation="2"
              class="text-none font-weight-bold px-6"
              :loading="loading"
              @click="submitForm"
            >
              <v-icon left size="18">mdi-cloud-upload-outline</v-icon>
              Publish Product
            </v-btn>

            <!-- Other steps → next -->
            <v-btn
              v-else
              color="primary"
              rounded
              elevation="2"
              class="text-none font-weight-bold px-6"
              @click="nextStep"
            >
              Next<v-icon right size="18">mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </div>

      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from 'axios'
import { VueEditor } from 'vue2-editor'
import Spinner from '@/components/Spinner.vue'

/* ── Tiny helper sub-components (defined inline) ──────── */
const StepTitle = {
  functional: true,
  props: ['icon', 'title', 'subtitle'],
  render(h, ctx) {
    return h('div', { class: 'mb-5' }, [
      h('div', { class: 'd-flex align-center mb-1' }, [
        h('v-icon', { props: { color: 'primary', size: 20 }, class: 'me-2' }, [ctx.props.icon]),
        h('span', { class: 'text-subtitle-1 font-weight-bold' }, [ctx.props.title]),
      ]),
      h('p', { class: 'text-body-2 grey--text ma-0' }, [ctx.props.subtitle]),
      h('v-divider', { class: 'mt-3 mb-4' }),
    ])
  },
}

const ReviewRow = {
  functional: true,
  props: ['label', 'value'],
  render(h, ctx) {
    return h('div', { class: 'd-flex align-start py-1' }, [
      h('span', { class: 'text-caption grey--text me-2', style: 'min-width:80px;flex-shrink:0' },
        [ctx.props.label + ':']),
      h('span', {
        class: 'text-caption font-weight-bold text--primary text-truncate',
        style: 'max-width:200px',
      }, [ctx.props.value || '—']),
    ])
  },
}

export default {
  components: { VueEditor, Spinner, StepTitle, ReviewRow },

  data() {
    return {
      currentStep: 1,
      totalSteps: 4,
      steps: [
        { label: 'Basic Info' },
        { label: 'Pricing' },
        { label: 'Media' },
        { label: 'Description' },
      ],

      form: {
        selectedCategory: '',
        product_name: '',
        supplier_contact: '237',
        key_words: '',
        product_price: '',
        product_wholesale_price: '',
        product_no_stock: '',
        product_cover_image: null,
        product_video: null,
        product_images: [],
        product_description: '',
      },

      errors: {},
      loading: false,
      categoriesLoading: false,
      categoryItems: [],

      // Media previews
      coverPreview: null,
      videoPreview: null,
      imagePreviews: [],
    }
  },

  computed: {
    margin() {
      const sell  = parseFloat(this.form.product_price)
      const buy   = parseFloat(this.form.product_wholesale_price)
      if (!sell || !buy) return null
      return sell - buy
    },
    marginPct() {
      const sell = parseFloat(this.form.product_price)
      if (!sell) return 0
      return ((this.margin / sell) * 100).toFixed(0)
    },
    categoryName() {
      const cat = this.categoryItems.find(c => c.id === this.form.selectedCategory)
      return cat ? cat.name : '—'
    },
  },

  mounted() {
    document.title = 'Manager: Market Place | Add Product'
    this.getProductCategories()
  },

  methods: {
    // ── Navigation ──────────────────────────────────────────────────────
    nextStep() {
      if (!this.validateStep(this.currentStep)) return
      this.currentStep++
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
    },
    prevStep() {
      this.currentStep--
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
    },
    goToStep(step) {
      // Only allow going back to completed steps
      if (step < this.currentStep) this.currentStep = step
    },

    // ── Validation (per step) ───────────────────────────────────────────
    validateStep(step) {
      this.errors = {}
      let valid = true
      const set = (field, msg) => { this.errors[field] = msg; valid = false }

      if (step === 1) {
        if (!this.form.selectedCategory)    set('selectedCategory', 'Please select a category.')
        if (!this.form.product_name.trim()) set('product_name', 'Product name is required.')
        else if (this.form.product_name.trim().length < 3)
          set('product_name', 'Name must be at least 3 characters.')
        if (!this.form.supplier_contact)
          set('supplier_contact', 'Please enter the WhatsApp contact.')
        else if (!/^[\d]{9,15}$/.test(String(this.form.supplier_contact).replace(/\s/g, '')))
          set('supplier_contact', 'Enter a valid phone number (9–15 digits).')
        if (!this.form.key_words.trim())    set('key_words', 'Please enter at least one keyword.')
      }

      if (step === 2) {
        const price = parseFloat(this.form.product_price)
        const wprice = parseFloat(this.form.product_wholesale_price)
        const stock  = parseInt(this.form.product_no_stock)
        if (!this.form.product_price || isNaN(price) || price <= 0)
          set('product_price', 'Enter a valid selling price greater than 0.')
        if (!this.form.product_wholesale_price || isNaN(wprice) || wprice <= 0)
          set('product_wholesale_price', 'Enter a valid buy price greater than 0.')
        if (!this.form.product_no_stock || isNaN(stock) || stock < 0)
          set('product_no_stock', 'Enter a valid stock quantity (0 or more).')
      }

      if (step === 3) {
        if (!this.form.product_cover_image)
          set('product_cover_image', 'A cover image is required.')
        else {
          const ext = this.form.product_cover_image.name.split('.').pop().toLowerCase()
          if (!['jpg','jpeg','png','webp','gif'].includes(ext))
            set('product_cover_image', 'Please upload a valid image (JPG, PNG, WEBP, GIF).')
        }
        if (!this.form.product_images || !this.form.product_images.length)
          set('product_images', 'Please upload at least one additional image.')
      }

      if (step === 4) {
        const stripped = this.form.product_description.replace(/<[^>]*>/g, '').trim()
        if (!stripped || stripped.length < 20)
          set('product_description', 'Description must be at least 20 characters.')
      }

      if (!valid) {
        this.$store.commit('setSnackBarMessage', 'Please fix the errors before continuing.')
        this.$store.commit('setSnackBarColor', 'orange darken-2')
        this.$store.commit('activateSnackBar', true)
      }
      return valid
    },

    clearError(field) {
      if (this.errors[field]) {
        const e = { ...this.errors }
        delete e[field]
        this.errors = e
      }
    },

    // ── Media handlers ──────────────────────────────────────────────────
    onCoverChange(file) {
      this.clearError('product_cover_image')
      if (!file) { this.coverPreview = null; return }
      this.coverPreview = URL.createObjectURL(file)
    },
    removeCover() {
      this.form.product_cover_image = null
      this.coverPreview = null
    },
    onVideoChange(file) {
      if (!file) { this.videoPreview = null; return }
      this.videoPreview = URL.createObjectURL(file)
    },
    removeVideo() {
      this.form.product_video = null
      this.videoPreview = null
    },
    onImagesChange(files) {
      this.clearError('product_images')
      if (!files || !files.length) { this.imagePreviews = []; return }
      this.imagePreviews = files.map(f => URL.createObjectURL(f))
    },
    removeExtraImage(idx) {
      const newFiles = [...this.form.product_images]
      newFiles.splice(idx, 1)
      this.form.product_images = newFiles
      this.imagePreviews.splice(idx, 1)
      if (!newFiles.length) this.errors = { ...this.errors, product_images: 'Please upload at least one additional image.' }
    },

    // ── Helpers ──────────────────────────────────────────────────────────
    formatPrice(price) {
      if (!price) return '0 XAF'
      return new Intl.NumberFormat('en-US').format(price) + ' XAF'
    },
    showSnackbar(msg, color) {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    },

    // ── API ───────────────────────────────────────────────────────────────

    // ✅ URL corrigée
    async getProductCategories() {
      this.categoriesLoading = true
      try {
        const res = await axios.get('/api/v1/manager/market_place/categories/')
        this.categoryItems = res.data
      } catch (error) {
        this.showSnackbar('Error loading categories. Please refresh.', 'red darken-4')
      } finally {
        this.categoriesLoading = false
      }
    },

    async submitForm() {
      if (!this.validateStep(this.totalSteps)) return

      this.loading = true
      try {
        const formData = new FormData()
        formData.append('product_name',          this.form.product_name)
        formData.append('supplier_contact',       this.form.supplier_contact)
        formData.append('product_cover_image',    this.form.product_cover_image)
        formData.append('selectedCategory',       this.form.selectedCategory)
        formData.append('product_no_stock',       this.form.product_no_stock)
        formData.append('product_description',    this.form.product_description)
        formData.append('key_words',              this.form.key_words)
        formData.append('product_price',          this.form.product_price)
        formData.append('product_wholesale_price',this.form.product_wholesale_price)

        if (this.form.product_video) {
          formData.append('product_video', this.form.product_video)
        }

        this.form.product_images.forEach((file, i) => {
          formData.append('product_images' + String(i), file)
        })
        formData.append('product_images_length', this.form.product_images.length)

        const response = await axios.post(
          '/api/v1/manager/market_place/add/product/',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )

        this.$store.commit('setSnackBarMessage', response.data[0].text)
        this.$store.commit('setSnackBarColor',   response.data[0].color)
        this.$store.commit('activateSnackBar', true)
        this.$store.commit('setSnackBarTimeOut', 8000)

        if (response.data[0].category === 'success') {
          const id = response.data[0].product_id
          setTimeout(() => this.$router.push('/market_place/products/detail/' + id), 2500)
        }
      } catch (error) {
        if (error.response) {
          this.showSnackbar(error.response.status + ' ' + error.response.statusText, 'red darken-4')
        } else {
          this.showSnackbar('Network error. Please check your connection.', 'red darken-4')
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Step header ────────────────────────────────────── */
.step-item { opacity: 0.45; transition: opacity 0.25s ease; }
.step-item.step-active,
.step-item.step-done { opacity: 1; }

.step-circle {
  width: 28px; height: 28px;
  border-radius: 50%;
  background: #e0e0e0;
  font-size: 12px;
  color: #757575;
  transition: background 0.25s ease, color 0.25s ease;
  flex-shrink: 0;
}
.step-active .step-circle {
  background: #1976d2;
  color: #fff;
  box-shadow: 0 0 0 4px #1976d220;
}
.step-done .step-circle {
  background: #43a047;
  color: #fff;
}

.step-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: #9e9e9e;
  white-space: nowrap;
}
.step-active .step-label  { color: #1976d2; }
.step-done   .step-label  { color: #43a047; }

/* ── Media zones ────────────────────────────────────── */
.media-zone {
  border: 1.5px dashed #bdbdbd;
  background: #fafafa;
  transition: border-color 0.2s ease;
}
.media-zone--error { border-color: #f44336 !important; }

.cover-preview-wrap { position: relative; display: inline-block; }
.cover-remove-btn {
  position: absolute !important;
  top: -8px; right: -8px;
}
.extra-thumb-wrap { position: relative; display: inline-block; }
.thumb-remove-btn {
  position: absolute !important;
  top: -6px; right: -6px;
  width: 18px !important; height: 18px !important;
  min-width: 0 !important;
}

/* ── Step transition ────────────────────────────────── */
.slide-fade-enter-active,
.slide-fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.slide-fade-enter  { opacity: 0; transform: translateX(18px); }
.slide-fade-leave-to { opacity: 0; transform: translateX(-18px); }
</style>